import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useAuth} from '../contexts/AuthContext';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Paper,
  Radio,
  Select,
  Snackbar,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Edit as EditIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import {collection, doc, getDoc, setDoc, updateDoc} from 'firebase/firestore';
import {getDownloadURL, ref, uploadBytes} from 'firebase/storage';
import {db, storage} from '../firebase-config';
import {DragDropContext, Draggable, Droppable} from '@hello-pangea/dnd';

export default function QuizEdit() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Generiere eine eindeutige ID
  const generateQuestionId = useCallback(() => {
    return `q_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
  }, []);

  const [quiz, setQuiz] = useState({
    title: '',
    description: '',
    questions: [],
    isPublic: false,
    category: 'general',
    difficulty: 'medium',
    imageUrl: '',
    tags: [],
    settings: {
      shuffleQuestions: false,
      shuffleAnswers: false,
      showExplanation: true,
      timeLimit: 0,
      passingScore: 70
    }
  });

  const [currentQuestion, setCurrentQuestion] = useState(() => ({
    text: '',
    type: 'single_choice',
    options: ['', ''],
    correctAnswers: [],
    points: 1,
    explanation: '',
    id: generateQuestionId()
  }));

  const [editingQuestionIndex, setEditingQuestionIndex] = useState(-1);
  const [imageFile, setImageFile] = useState(null);

  // Validiere Quiz-Daten
  const validateQuiz = () => {
    if (!quiz.title.trim()) {
      setError('Bitte geben Sie einen Titel ein');
      return false;
    }
    if (!quiz.description.trim()) {
      setError('Bitte geben Sie eine Beschreibung ein');
      return false;
    }
    if (quiz.questions.length === 0) {
      setError('Fügen Sie mindestens eine Frage hinzu');
      return false;
    }
    
    // Prüfe jede Frage
    for (let i = 0; i < quiz.questions.length; i++) {
      const question = quiz.questions[i];
      if (!question.text.trim()) {
        setError(`Frage ${i + 1} hat keinen Text`);
        return false;
      }
      if (question.options.length < 2) {
        setError(`Frage ${i + 1} benötigt mindestens 2 Antwortoptionen`);
        return false;
      }
      if (question.correctAnswers.length === 0) {
        setError(`Frage ${i + 1} hat keine korrekte Antwort markiert`);
        return false;
      }
      if (question.options.some(opt => !opt.trim())) {
        setError(`Frage ${i + 1} hat leere Antwortoptionen`);
        return false;
      }
    }
    
    return true;
  };

  // Lade Quiz-Daten
  useEffect(() => {
    const loadQuiz = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const quizDoc = await getDoc(doc(db, 'quizzes', quizId));
        if (quizDoc.exists()) {
          const quizData = quizDoc.data();
          if (quizData.createdBy !== currentUser?.uid) {
            setError('Sie haben keine Berechtigung, dieses Quiz zu bearbeiten');
            navigate('/my-quizzes');
            return;
          }

          // Stelle sicher, dass jede Frage eine stabile ID hat
          const questionsWithIds = (quizData.questions || []).map((q, index) => ({
            ...q,
            id: q.id || `q_${Date.now()}_${index}`
          }));

          setQuiz({
            ...quizData,
            questions: questionsWithIds,
            tags: quizData.tags || [],
            difficulty: quizData.difficulty === 'mittel' ? 'medium' : quizData.difficulty,
            settings: {
              shuffleQuestions: false,
              shuffleAnswers: false,
              showExplanation: true,
              timeLimit: 0,
              passingScore: 70,
              ...quizData.settings
            }
          });
        } else {
          setError('Quiz wurde nicht gefunden');
          navigate('/my-quizzes');
        }
      } catch (error) {
        console.error('Error loading quiz:', error);
        setError('Fehler beim Laden des Quiz: ' + error.message);
        navigate('/my-quizzes');
      } finally {
        setIsLoading(false);
      }
    };

    if (quizId) {
      loadQuiz();
    } else {
      setIsLoading(false);
    }
  }, [quizId, currentUser, navigate]);

  // Bild-Upload Handler
  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageFile(file);
      const imageUrl = URL.createObjectURL(file);
      setQuiz({ ...quiz, imageUrl });
    }
  };

  // Einstellungen Handler
  const handleSettingsChange = (field, value) => {
    setQuiz({
      ...quiz,
      settings: {
        ...quiz.settings,
        [field]: value
      }
    });
  };

  // Tags Handler
  const handleTagsChange = (event) => {
    const value = event.target.value;
    const tags = value.split(',').map(tag => tag.trim()).filter(tag => tag);
    setQuiz({ ...quiz, tags });
  };

  // Speichere Quiz-Änderungen
  const handleSaveQuiz = async () => {
    try {
      if (!validateQuiz()) {
        return;
      }

      setIsLoading(true);
      setError(null);
      
      let finalImageUrl = quiz.imageUrl || '';
      
      // Wenn ein neues Bild hochgeladen wurde
      if (imageFile) {
        try {
          const imageId = quizId || Date.now().toString();
          const storageRef = ref(storage, `quiz-images/${imageId}/${Date.now()}-${imageFile.name}`);
          const snapshot = await uploadBytes(storageRef, imageFile);
          finalImageUrl = await getDownloadURL(snapshot.ref);
        } catch (error) {
          console.error('Error uploading image:', error);
          setError('Fehler beim Hochladen des Bildes: ' + error.message);
          return;
        }
      }

      // Stelle sicher, dass alle Fragen eine ID haben
      const questionsWithIds = quiz.questions.map(question => ({
        ...question,
        id: question.id || generateQuestionId()
      }));

      // Aktualisiere das Quiz mit allen Änderungen
      const quizData = {
        title: quiz.title,
        description: quiz.description,
        category: quiz.category,
        difficulty: quiz.difficulty === 'medium' ? 'mittel' : quiz.difficulty,
        isPublic: Boolean(quiz.isPublic),
        imageUrl: finalImageUrl,
        tags: quiz.tags || [],
        settings: {
          shuffleQuestions: Boolean(quiz.settings.shuffleQuestions),
          shuffleAnswers: Boolean(quiz.settings.shuffleAnswers),
          showExplanation: Boolean(quiz.settings.showExplanation),
          timeLimit: Number(quiz.settings.timeLimit) || 0,
          passingScore: Number(quiz.settings.passingScore) || 70
        },
        questions: questionsWithIds,
        updatedAt: new Date().toISOString()
      };

      // Wenn es ein neues Quiz ist, füge createdBy und createdAt hinzu
      if (!quizId) {
        quizData.createdBy = currentUser.uid;
        quizData.createdAt = new Date().toISOString();
      }

      // Debug-Log
      console.log('Saving quiz data:', JSON.stringify(quizData, null, 2));

      // Speichere in der Datenbank
      if (quizId) {
        // Update existierendes Quiz
        await updateDoc(doc(db, 'quizzes', quizId), quizData);
      } else {
        // Erstelle neues Quiz
        const newQuizRef = doc(collection(db, 'quizzes'));
        await setDoc(newQuizRef, quizData);
      }

      // Aktualisiere den lokalen State mit den neuen IDs
      setQuiz(prev => ({
        ...prev,
        questions: questionsWithIds
      }));

      setSuccessMessage('Quiz wurde erfolgreich gespeichert');
      setTimeout(() => navigate('/my-quizzes'), 1500);
    } catch (error) {
      console.error('Error saving quiz:', error);
      setError('Fehler beim Speichern des Quiz: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuestionChange = (field, value) => {
    setCurrentQuestion(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...currentQuestion.options];
    newOptions[index] = value;
    setCurrentQuestion(prev => ({
      ...prev,
      options: newOptions
    }));
  };

  const handleAddOption = () => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };

  const handleRemoveOption = (index) => {
    const newOptions = currentQuestion.options.filter((_, i) => i !== index);
    const newCorrectAnswers = currentQuestion.correctAnswers.filter(i => i !== index);
    setCurrentQuestion(prev => ({
      ...prev,
      options: newOptions,
      correctAnswers: newCorrectAnswers
    }));
  };

  const handleCorrectAnswerChange = (index) => {
    let newCorrectAnswers = [...currentQuestion.correctAnswers];
    if (currentQuestion.type === 'single_choice') {
      newCorrectAnswers = [index];
    } else {
      const exists = newCorrectAnswers.includes(index);
      if (exists) {
        newCorrectAnswers = newCorrectAnswers.filter(i => i !== index);
      } else {
        newCorrectAnswers.push(index);
      }
    }
    setCurrentQuestion(prev => ({
      ...prev,
      correctAnswers: newCorrectAnswers
    }));
  };

  const handleSaveQuestion = () => {
    const questionToSave = {
      ...currentQuestion,
      id: currentQuestion.id || generateQuestionId()
    };
    
    setQuiz(prev => {
      const updatedQuestions = [...prev.questions];
      if (editingQuestionIndex === -1) {
        updatedQuestions.push(questionToSave);
      } else {
        updatedQuestions[editingQuestionIndex] = questionToSave;
      }
      
      return {
        ...prev,
        questions: updatedQuestions.map(q => ({
          ...q,
          id: q.id || generateQuestionId()
        }))
      };
    });
    
    // Reset das Formular
    setCurrentQuestion({
      text: '',
      type: 'single_choice',
      options: ['', ''],
      correctAnswers: [],
      points: 1,
      explanation: '',
      id: generateQuestionId()
    });
    setEditingQuestionIndex(-1);
  };

  // Drag & Drop Handler
  const handleDragEnd = useCallback((result) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    setQuiz(prev => {
      const updatedQuestions = Array.from(prev.questions);
      const [movedQuestion] = updatedQuestions.splice(sourceIndex, 1);
      updatedQuestions.splice(destinationIndex, 0, movedQuestion);
      return { ...prev, questions: updatedQuestions };
    });
  }, []);

  // Fragen-Liste Komponente
  const QuestionList = React.memo(({ questions, onEdit, onDelete }) => (
    <Droppable droppableId="questions-list">
      {(provided) => (
        <List
          {...provided.droppableProps}
          ref={provided.innerRef}
        >
          {questions.map((question, index) => (
            <Draggable 
              key={question.id}
              draggableId={question.id}
              index={index}
            >
              {(provided, snapshot) => (
                <ListItem
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                  sx={{
                    mb: 2,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: snapshot.isDragging ? 'action.hover' : 'background.default',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    width: '100%'
                  }}>
                    <DragIcon sx={{ color: 'text.secondary', mr: 2 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        {question.text}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {question.type === 'single_choice' ? 'Single Choice' : 'Multiple Choice'} • 
                        {question.options.length} Optionen • 
                        {question.points} {question.points === 1 ? 'Punkt' : 'Punkte'}
                      </Typography>
                    </Box>
                    <Box>
                      <IconButton onClick={() => onEdit(question, index)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => onDelete(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </ListItem>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
        </List>
      )}
    </Droppable>
  ));

  QuestionList.displayName = 'QuestionList';

  if (isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '80vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box sx={{ 
        p: 3,
        maxWidth: '1500px !important',
        mx: 'auto',
        width: '100%',
        px: { xs: 2, sm: 3, md: 4 }
      }}>
        <Grid container spacing={3}>
          {/* Linke Spalte - Quiz Metadaten */}
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                border: 1,
                borderColor: 'divider',
                borderRadius: 2
              }}
            >
              <Typography variant="h5" component="h1" gutterBottom sx={{ 
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #cb7e00, #ffa726)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Quiz-Details
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Titel"
                    value={quiz.title}
                    onChange={(e) => setQuiz({ ...quiz, title: e.target.value })}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Beschreibung"
                    value={quiz.description}
                    onChange={(e) => setQuiz({ ...quiz, description: e.target.value })}
                    multiline
                    rows={2}
                  />
                </Grid>

                {/* Bild-Upload */}
                <Grid item xs={12}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="quiz-image"
                    type="file"
                    onChange={handleImageUpload}
                  />
                  <Box
                    component="label"
                    htmlFor="quiz-image"
                    sx={{ 
                      display: 'block',
                      cursor: 'pointer',
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 1,
                      height: '100px',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    {quiz.imageUrl ? (
                      <Box
                        component="img"
                        src={quiz.imageUrl}
                        alt="Quiz-Vorschau"
                        sx={{ maxHeight: '80px', maxWidth: '100%', objectFit: 'contain' }}
                      />
                    ) : (
                      <Typography color="text.secondary">Quiz-Bild hochladen</Typography>
                    )}
                  </Box>
                </Grid>

                {/* Kategorie und Schwierigkeit */}
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Kategorie</InputLabel>
                    <Select
                      value={quiz.category}
                      label="Kategorie"
                      onChange={(e) => setQuiz({ ...quiz, category: e.target.value })}
                    >
                      <MenuItem value="general">Allgemein</MenuItem>
                      <MenuItem value="science">Wissenschaft</MenuItem>
                      <MenuItem value="history">Geschichte</MenuItem>
                      <MenuItem value="sports">Sport</MenuItem>
                      <MenuItem value="entertainment">Unterhaltung</MenuItem>
                      <MenuItem value="gaming">Gaming</MenuItem>
                      <MenuItem value="movies">Filme</MenuItem>
                      <MenuItem value="books">Bücher</MenuItem>
                      <MenuItem value="internet">Internet</MenuItem>
                      <MenuItem value="knowledge">Wissen</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Schwierigkeit</InputLabel>
                    <Select
                      value={quiz.difficulty}
                      onChange={(e) => setQuiz({ ...quiz, difficulty: e.target.value })}
                      label="Schwierigkeit"
                    >
                      <MenuItem value="easy">Leicht</MenuItem>
                      <MenuItem value="medium">Mittel</MenuItem>
                      <MenuItem value="hard">Schwer</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Tags */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Tags (durch Komma getrennt)"
                    value={(quiz.tags || []).join(', ')}
                    onChange={handleTagsChange}
                    helperText="z.B.: Geschichte, Deutschland, Mittelalter"
                  />
                </Grid>

                {/* Quiz-Einstellungen */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                    Quiz-Einstellungen
                  </Typography>
                  
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 2, 
                      bgcolor: 'background.default',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 2
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Zeitlimit</InputLabel>
                          <Select
                            value={quiz.settings.timeLimit}
                            onChange={(e) => handleSettingsChange('timeLimit', e.target.value)}
                            label="Zeitlimit"
                          >
                            <MenuItem value={0}>Kein Limit</MenuItem>
                            <MenuItem value={15}>15 Minuten</MenuItem>
                            <MenuItem value={30}>30 Minuten</MenuItem>
                            <MenuItem value={45}>45 Minuten</MenuItem>
                            <MenuItem value={60}>60 Minuten</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Bestehensgrenze</InputLabel>
                          <Select
                            value={quiz.settings.passingScore}
                            onChange={(e) => handleSettingsChange('passingScore', e.target.value)}
                            label="Bestehensgrenze"
                          >
                            <MenuItem value={50}>50%</MenuItem>
                            <MenuItem value={60}>60%</MenuItem>
                            <MenuItem value={70}>70%</MenuItem>
                            <MenuItem value={80}>80%</MenuItem>
                            <MenuItem value={90}>90%</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Paper>

                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 2, 
                      bgcolor: 'background.default',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      Weitere Optionen
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={quiz.settings.shuffleQuestions}
                              onChange={(e) => handleSettingsChange('shuffleQuestions', e.target.checked)}
                            />
                          }
                          label={
                            <Typography variant="body2">Fragen mischen</Typography>
                          }
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={quiz.settings.shuffleAnswers}
                              onChange={(e) => handleSettingsChange('shuffleAnswers', e.target.checked)}
                            />
                          }
                          label={
                            <Typography variant="body2">Antworten mischen</Typography>
                          }
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={quiz.settings.showExplanation}
                              onChange={(e) => handleSettingsChange('showExplanation', e.target.checked)}
                            />
                          }
                          label={
                            <Typography variant="body2">Erklärungen</Typography>
                          }
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={quiz.isPublic}
                              onChange={(e) => setQuiz({ ...quiz, isPublic: e.target.checked })}
                            />
                          }
                          label={
                            <Typography variant="body2">Öffentlich</Typography>
                          }
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Speichern Button */}
                <Grid item xs={12}>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    mt: 2,
                    '& .MuiButton-root': {
                      minWidth: 'auto'
                    }
                  }}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/my-quizzes')}
                      sx={{ mr: 'auto' }}
                    >
                      Abbrechen
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveQuiz}
                      disabled={isLoading}
                    >
                      Quiz speichern
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Rechte Spalte - Fragen */}
          <Grid item xs={12} md={8}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 3,
              pb: 2,
              borderBottom: 1,
              borderColor: 'divider'
            }}>
              <Typography variant="h5" component="h2">
                Fragen ({quiz.questions.length})
              </Typography>
            </Box>

            {/* Fragen-Liste */}
            {quiz.questions.length > 0 && (
              <Paper 
                elevation={0}
                sx={{ 
                  p: 3, 
                  mb: 3,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2
                }}
              >
                <QuestionList 
                  questions={quiz.questions} 
                  onEdit={(question, index) => {
                    setCurrentQuestion({...question});
                    setEditingQuestionIndex(index);
                  }}
                  onDelete={(index) => {
                    setQuiz(prev => ({
                      ...prev,
                      questions: prev.questions.filter((_, i) => i !== index)
                    }));
                  }}
                />
              </Paper>
            )}

            {/* Fragen-Formular */}
            <Paper 
              elevation={0}
              sx={{ 
                p: 3,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2
              }}
            >
              <Typography variant="h6" gutterBottom>
                {editingQuestionIndex === -1 ? 'Neue Frage hinzufügen' : 'Frage bearbeiten'}
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Frage"
                    value={currentQuestion.text}
                    onChange={(e) => handleQuestionChange('text', e.target.value)}
                    multiline
                    rows={2}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Fragetyp</InputLabel>
                    <Select
                      value={currentQuestion.type}
                      onChange={(e) => handleQuestionChange('type', e.target.value)}
                      label="Fragetyp"
                    >
                      <MenuItem value="single_choice">Single Choice</MenuItem>
                      <MenuItem value="multiple_choice">Multiple Choice</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Punkte"
                    value={currentQuestion.points}
                    onChange={(e) => handleQuestionChange('points', parseInt(e.target.value) || 1)}
                    InputProps={{ inputProps: { min: 1 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>Antwortoptionen</Typography>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 2,
                      bgcolor: 'background.default',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1
                    }}
                  >
                    {currentQuestion.options.map((option, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {currentQuestion.type === 'single_choice' ? (
                          <Radio
                            checked={currentQuestion.correctAnswers.includes(index)}
                            onChange={() => handleCorrectAnswerChange(index)}
                            size="small"
                          />
                        ) : (
                          <Checkbox
                            checked={currentQuestion.correctAnswers.includes(index)}
                            onChange={() => handleCorrectAnswerChange(index)}
                            size="small"
                          />
                        )}
                        <TextField
                          fullWidth
                          size="small"
                          value={option}
                          onChange={(e) => handleOptionChange(index, e.target.value)}
                          placeholder={`Option ${index + 1}`}
                          sx={{ mx: 1 }}
                        />
                        <IconButton 
                          size="small"
                          onClick={() => handleRemoveOption(index)}
                          disabled={currentQuestion.options.length <= 2}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={handleAddOption}
                      size="small"
                      sx={{ mt: 1 }}
                    >
                      Option hinzufügen
                    </Button>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Erklärung"
                    value={currentQuestion.explanation}
                    onChange={(e) => handleQuestionChange('explanation', e.target.value)}
                    multiline
                    rows={2}
                    helperText="Optional: Erklärung zur richtigen Antwort"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      onClick={handleSaveQuestion}
                      startIcon={<SaveIcon />}
                    >
                      {editingQuestionIndex === -1 ? 'Frage hinzufügen' : 'Frage aktualisieren'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        {/* Error/Success Messages */}
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
        >
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>
        
        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={() => setSuccessMessage('')}
        >
          <Alert severity="success" onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        </Snackbar>
      </Box>
    </DragDropContext>
  );
} 
